// Home (FW-006, FW-008): a composed page of cards on the frame, not a stack. A greeting; the day's
// cards as one carousel (today's verse, the reflection, the quote, the catechism, today's plan
// reading), the height of the window's room, never a tower; a composer card that opens the
// composer for a verse, a note or today's verse; then friends' posts as cards, newest first, with
// the "new posts" pill that waits instead of pushing the page. The feed ends.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Avatar } from "../../components/Avatar";
import { Button, LinkButton } from "../../components/Button";
import { EmptyState } from "../../components/Empty";
import { Icon } from "../../components/Icon";
import { Post } from "../../components/Post";
import { PostSkeleton } from "../../components/Skeleton";
import { Today } from "../../components/Today";
import { useConfig, useFeed, useNewCount, useSession } from "../../data/queries";
import { useTitle } from "../../lib/useTitle";
import { motionStyles } from "../../motion";
import s from "./home.module.css";

export const Route = createFileRoute("/_app/home")({
  component: Home,
});

/** "Good morning", "Good afternoon" or "Good evening", by the reader's own clock. */
function greeting(now = new Date()) {
  const h = now.getHours();
  return h < 5 ? "Good evening" : h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

function Home() {
  useTitle("Home");
  const { data: feed, isPending, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useFeed();
  const { data: session } = useSession();
  const mine = (id: string) => id === session?.user.id;
  // Posts that arrive while reading wait behind a quiet pill instead of pushing the page down: the
  // feed shows what's no newer than the newest post seen, and earlier pages load below.
  const [seenUntil, setSeenUntil] = useState<string | null>(null);
  const newestOf = (items: { createdAt: string }[]) => items.reduce((m, p) => (p.createdAt > m ? p.createdAt : m), "") || new Date().toISOString();
  if (feed && seenUntil === null) setSeenUntil(newestOf(feed.items));
  const shown = feed && seenUntil ? feed.items.filter((p) => p.createdAt <= seenUntil || mine(p.author.id)) : [];
  const fresh = feed && seenUntil ? feed.items.filter((p) => p.createdAt > seenUntil && !mine(p.author.id)) : [];
  // Friends' posts since then, counted by the API each minute while the tab is visible (app flow §3).
  const { data: waiting = 0 } = useNewCount(seenUntil);
  const freshCount = Math.max(waiting, fresh.length);
  const showFresh = async () => {
    const next = waiting > fresh.length ? (await refetch()).data : feed;
    setSeenUntil(newestOf(next?.items ?? []));
    window.scrollTo({ top: 0, behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  };
  const name = session?.user.displayName.split(" ")[0];

  return (
    <div className={s.home} data-surface="cards">
      <header className={s.greet}>
        <h1 className={s.hello}>
          {greeting()}
          {name && (
            <>
              , <em>{name}</em>
            </>
          )}
        </h1>
      </header>
      <div className={s.freshSlot} aria-live="polite">
        {freshCount > 0 && (
          <button type="button" className={s.fresh} onClick={() => void showFresh()}>
            <Icon name="caret" size={12} />
            {freshCount === 1 ? "1 new post" : `${freshCount} new posts`}
          </button>
        )}
      </div>
      <Today />
      <Compose />

      <section className={s.feedSection} aria-labelledby="feed-head">
        <div className={s.feedHead}>
          <h2 id="feed-head" className={s.feedTitle}>
            From your friends
          </h2>
          <p className={s.keys} aria-hidden="true">
            <kbd>J</kbd>
            <kbd>K</kbd> to move
          </p>
        </div>
        {isPending ? (
          <PostSkeleton />
        ) : shown.length > 0 ? (
          <>
            <div className={`${s.feed} ${motionStyles.arrive}`}>
              {shown.map((p) => (
                <Post key={p.id} post={p} />
              ))}
            </div>
            {hasNextPage ? (
              <p className={s.more}>
                <Button busy={isFetchingNextPage} disabled={isFetchingNextPage} onClick={() => void fetchNextPage()}>
                  {isFetchingNextPage ? "Loading earlier posts" : "Show earlier posts"}
                </Button>
              </p>
            ) : (
              <p className={s.end}>You’re up to date.</p>
            )}
          </>
        ) : (
          <div className={s.emptyCard}>
            <EmptyState
              title="Fellowship is better with friends."
              body="When the people you know share what they’re reading, it will be here."
              verse={{
                text: "Let’s consider how to provoke one another to love and good works, not forsaking our own assembling together…",
                ref: "Hebrews 10:24–25 · WEB",
              }}
            >
              <LinkButton to="/friends" search={{ tab: "find" }} variant="primary">
                Find people
              </LinkButton>
            </EmptyState>
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * The composer's front door, as the feed's first card: your face, a line that opens the composer,
 * and the kinds of post under it; Photo (FW-007) while the social.images switch is on.
 */
function Compose() {
  const { data: session } = useSession();
  const { data: config } = useConfig();
  const photos = config?.features?.["social.images"]?.state === "on";
  if (!session) return null;
  return (
    <section className={s.compose} aria-label="Share with your friends">
      <div className={s.composeRow}>
        <Avatar user={session.user} size={42} />
        <Link to="/new" className={s.prompt}>
          What are you reading today?
        </Link>
      </div>
      <ul className={s.kinds}>
        <li>
          <Link to="/new" search={{ kind: "verse" }} className={s.kind}>
            <Icon name="bible" size={18} />
            Verse
          </Link>
        </li>
        <li>
          <Link to="/new" search={{ kind: "note" }} className={s.kind}>
            <Icon name="note" size={18} />
            Note
          </Link>
        </li>
        {photos && (
          <li>
            <Link to="/new" search={{ kind: "note" }} className={s.kind}>
              <Icon name="image" size={18} />
              Photo
            </Link>
          </li>
        )}
        <li>
          <Link to="/new" search={{ kind: "votd" }} className={s.kind}>
            <Icon name="repost" size={18} />
            Today’s verse
          </Link>
        </li>
        <li className={s.kindEnd}>
          <Link to="/reflect" className={s.kind}>
            <Icon name="spark" size={18} />
            Reflect
          </Link>
        </li>
      </ul>
    </section>
  );
}
